import { uid as getUid } from "@dhx/ts-common/core";
import { sv, VNode } from "@dhx/ts-common/dom";
import {
	getClassesForRotateScale,
	getFontStyle,
	getTextWidth,
	verticalCenteredText,
} from "../helpers/common";
import { IGridRenderConfig, IScaleConfig } from "../types";

const defaultTextTemplate = <T>(item: T): string => item.toString();

type PointInfo = [number, string];

const getScaleAriaAttrs = (axis: string, text: any) => ({
	role: "graphics-object",
	"aria-label": `${axis}-axis${text ? ", " + text : ""}`,
});

export function bottom(points: PointInfo[], config: IScaleConfig, width: number, height: number): VNode {
	const { title, textPadding, scalePadding, textTemplate, showText, scaleRotate } = config;
	const template = textTemplate || defaultTextTemplate;
	let text = [];
	let extraTittlePadding = 0;
	if (showText) {
		extraTittlePadding = textPadding;
		const canRotate = scaleRotate && !isNaN(scaleRotate);
		const y = height + textPadding;
		text = points.map(p => {
			const x = p[0];
			const transform = canRotate ? `rotate(${scaleRotate} ${x} ${y})` : "";
			const classList = ["scale-text", "top-text"];
			if (canRotate) {
				const angle = scaleRotate % 360;
				classList.push(getClassesForRotateScale("bottom", angle));
			}
			return sv("text", { x, y, class: classList.join(" "), transform }, [
				verticalCenteredText(template(p[1])),
			]);
		});
	}
	const id = getUid();
	let svTitle = null;
	const mainLine = sv("path", {
		class: "main-scale",
		d: `M0 ${height} H${width - 0.5}`,
		id,
	});
	if (title) {
		svTitle = sv("text", { dx: width / 2, dy: scalePadding + extraTittlePadding }, [
			sv("textPath", { href: `#${id}`, class: "scale-title " }, title),
		]);
	}
	return sv(
		"g",
		{
			...getScaleAriaAttrs("x", title || config.text),
		},
		[mainLine, svTitle].concat(text)
	);
}

export function bottomGrid(
	points: PointInfo[],
	width: number,
	height: number,
	config: IGridRenderConfig
): VNode {
	const { dashed, grid, targetLine, targetValue } = config;

	const len = points.length;
	const gridLines = [];
	const className = `grid-line ${dashed ? "dash-line" : ""} ${!grid ? "hidden-line" : ""}`;
	for (let i = 0; i < len; i++) {
		if (i === 0 && points[i][0] === 0 && !config.hidden) {
			continue;
		}
		if (i === targetLine) {
			const d = `M${points[i][0]} 0 V ${height}`;
			const path = sv("path", { d, class: `${className} spec-grid-line` });
			gridLines.push(path);
			continue;
		}

		const d = `M${points[i][0]} 0 V ${height}`;
		const path = sv("path", { d, class: className, _ref: "line" + Math.round(points[i][0]) });
		gridLines.push(path);
		if (i === len - 1 && points[i][0] !== width) {
			const additionD = `M${width} 0 V ${height}`;
			const additionPath = sv("path", { d: additionD, class: className });
			gridLines.push(additionPath);
		}
	}
	if (targetValue) {
		const d = `M${targetValue * width} 0 V ${height}`;
		const path = sv("path", { d, class: `${className} spec-grid-line` });
		gridLines.push(path);
	}
	return sv("g", gridLines);
}

export function top(points: PointInfo[], config: IScaleConfig, width: number, _height: number): VNode {
	const { title, textPadding, scalePadding, textTemplate, showText, scaleRotate } = config;

	const template = textTemplate || defaultTextTemplate;
	let text = [];
	let extraTittlePadding = 0;
	if (showText) {
		extraTittlePadding = textPadding;
		const canRotate = scaleRotate && !isNaN(scaleRotate);
		const y = -textPadding;

		text = points.map(p => {
			const classList = ["scale-text"];
			const x = p[0];
			const transform = canRotate ? `rotate(${scaleRotate} ${x} ${y})` : "";
			if (canRotate) {
				const angle = scaleRotate % 360;
				classList.push(getClassesForRotateScale("top", angle));
			}
			return sv("text", { x, y, class: classList.join(" "), transform }, [
				verticalCenteredText(template(p[1])),
			]);
		});
	}
	const id = getUid();
	const mainLine = sv("path", { d: `M0 0 H${width}`, class: "main-scale", id });
	let svTitle = null;
	if (title) {
		svTitle = sv("text", { dx: width / 2, dy: -scalePadding - extraTittlePadding }, [
			sv("textPath", { href: `#${id}`, class: "scale-title" }, title),
		]);
	}
	return sv(
		"g",
		{
			...getScaleAriaAttrs("x", title || config.text),
		},
		[mainLine, svTitle].concat(text)
	);
}

export function topGrid(
	points: PointInfo[],
	_width: number,
	height: number,
	config: IGridRenderConfig
): VNode {
	const { dashed, grid, targetLine } = config;

	const len = points.length;
	const gridLines = [];
	const className = `grid-line ${dashed ? "dash-line" : ""} ${!grid ? "hidden-line" : ""}`;
	for (let i = 0; i < len; i++) {
		if (i === 0 && points[i][0] === 0 && !config.hidden) {
			continue;
		}
		if (i === targetLine) {
			const d = `M${points[i][0]} 0 V ${height}`;
			const path = sv("path", { d, class: `${className} spec-grid-line` });
			gridLines.push(path);
			continue;
		}
		const d = `M${points[i][0]} 0 V ${height}`;
		const path = sv("path", { d, class: className, _ref: "line" + Math.round(points[i][0]) });
		gridLines.push(path);
		if (i === len - 1 && points[i][0] !== 0) {
			const additionD = `M0 0 V ${height}`;
			const additionPath = sv("path", { d: additionD, class: className });
			gridLines.push(additionPath);
		}
	}
	return sv("g", gridLines);
}

export function left(points: PointInfo[], config: IScaleConfig, _width: number, height: number): VNode {
	const { title, textPadding, scalePadding, textTemplate, showText, scaleRotate } = config;

	const template = textTemplate || defaultTextTemplate;
	let text = [];
	let extraTittlePadding = 0;
	if (showText) {
		const style = getFontStyle("scale-text");
		let maxTextWidth = 0;
		const canRotate = scaleRotate && !isNaN(scaleRotate);
		text = points.map(p => {
			const y = p[0];
			const x = -textPadding;
			const transform = canRotate ? `rotate(${scaleRotate} ${x} ${y})` : "";
			const classList = ["scale-text"];
			const scaleText = template(p[1]);
			if (title) {
				const textWidth = getTextWidth(scaleText, style);
				if (maxTextWidth < textWidth) {
					maxTextWidth = textWidth;
				}
			}
			if (canRotate) {
				const angle = scaleRotate % 360;
				classList.push(getClassesForRotateScale("left", angle));
			} else {
				classList.push("end-text");
			}
			return sv("text", { x, y, class: classList.join(" "), transform }, [
				verticalCenteredText(scaleText),
			]);
		});
		extraTittlePadding = maxTextWidth + textPadding;
	}
	const id = getUid();
	const mainLine = sv("path", {
		class: "main-scale",
		d: `M0 ${height} V 0.5`,
		id,
		_ref: points.length ? "line0" : null,
	}); // 0.5 instead of 0, coz stroke-linecap: square and dirrent stroke size
	let svTitle = null;
	if (title) {
		svTitle = sv("text", { dx: height / 2, dy: -scalePadding - extraTittlePadding }, [
			sv("textPath", { href: `#${id}`, class: "scale-title" }, title),
		]);
	}
	return sv(
		"g",
		{
			...getScaleAriaAttrs("y", title || config.text),
		},
		[mainLine, svTitle].concat(text)
	);
}

export function leftGrid(
	points: PointInfo[],
	width: number,
	height: number,
	config: IGridRenderConfig
): VNode {
	const { dashed, grid, targetLine, targetValue } = config;
	const len = points.length;
	const gridLines = [];
	const className = `grid-line ${dashed ? "dash-line" : ""}`;
	for (let i = 0; i < len; i++) {
		if (i === 0 && points[i][0] === height && !config.hidden) {
			continue;
		}
		if (targetLine === i) {
			const d = `M0 ${points[i][0]} H ${width}`;
			const path = sv("path", { d, class: `${className} spec-grid-line` });
			gridLines.push(path);
			continue;
		}
		if (grid) {
			const d = `M0 ${points[i][0]} H ${width}`;
			const path = sv("path", { d, class: className });
			gridLines.push(path);
			if (i === len - 1 && points[i][0] !== width) {
				const additionD = `M0 0 H${width}`;
				const additionPath = sv("path", { d: additionD, class: className });
				gridLines.push(additionPath);
			}
		}
	}
	if (targetValue) {
		const d = `M0 ${targetValue * height} H ${width}`;
		const path = sv("path", { d, class: `${className} spec-grid-line` });
		gridLines.push(path);
	}
	return sv("g", gridLines);
}

export function right(points: PointInfo[], config: IScaleConfig, width: number, height: number): VNode {
	const { title, textPadding, scalePadding, textTemplate, showText, scaleRotate } = config;

	const template = textTemplate || defaultTextTemplate;
	let text = [];
	let extraTittlePadding = 0;
	if (showText) {
		const style = getFontStyle("scale-text");
		let maxTextWidth = 0;
		const canRotate = scaleRotate && !isNaN(scaleRotate);
		text = points.map(p => {
			const scaleText = template(p[1]);
			const y = p[0];
			const x = width + textPadding;
			const transform = canRotate ? `rotate(${scaleRotate} ${x} ${y})` : "";
			const classList = ["scale-text"];
			if (title) {
				const textWidth = getTextWidth(scaleText, style);
				if (maxTextWidth < textWidth) {
					maxTextWidth = textWidth;
				}
			}
			if (canRotate) {
				const angle = scaleRotate % 360;
				classList.push(getClassesForRotateScale("right", angle));
			} else {
				classList.push("start-text");
			}
			return sv("text", { x, y, class: classList.join(" "), transform }, [
				verticalCenteredText(scaleText),
			]);
		});
		extraTittlePadding = textPadding + maxTextWidth;
	}
	const id = getUid();
	const mainLine = sv("path", {
		d: `M${width} ${height} V 0`,
		class: "main-scale",
		id,
		_ref: points.length ? "line0" : null,
	});
	let svTitle = null;
	if (title) {
		svTitle = sv("text", { dx: height / 2, dy: scalePadding + extraTittlePadding }, [
			sv("textPath", { href: `#${id}`, class: "scale-title" }, title),
		]);
	}
	return sv(
		"g",
		{
			...getScaleAriaAttrs("y", title || config.text),
		},
		[mainLine, svTitle].concat(text)
	);
}

export function rightGrid(
	points: PointInfo[],
	width: number,
	height: number,
	config: IGridRenderConfig
): VNode {
	const { dashed, grid, targetLine } = config;

	const len = points.length;
	const gridLines = [];
	const className = `grid-line ${dashed ? "dash-line" : ""}`;
	for (let i = 0; i < len; i++) {
		if (i === 0 && points[i][0] === height && !config.hidden) {
			continue;
		}
		if (targetLine === i) {
			const d = `M0 ${points[i][0]} H ${width}`;
			const path = sv("path", { d, class: `${className} spec-grid-line` });
			gridLines.push(path);
			continue;
		}
		if (grid) {
			const d = `M0 ${points[i][0]} H ${width}`;
			const path = sv("path", { d, class: className });
			gridLines.push(path);
			if (i === len - 1 && points[i][0] !== width) {
				const additionD = `M0 0 H${width}`;
				const additionPath = sv("path", { d: additionD, class: className });
				gridLines.push(additionPath);
			}
		}
	}
	return sv("g", gridLines);
}
